import type { Coords } from '@/shared/graphql/router/graphql'

interface Point {
  x: number
  y: number
}

function mergeCoordsGroup(coords: Coords[]): Coords {
  if (coords.length === 1) return coords[0]

  return {
    x1: Math.min(...coords.map((c) => c.x1)),
    y1: Math.min(...coords.map((c) => c.y1)),
    x2: Math.max(...coords.map((c) => c.x2)),
    y2: Math.max(...coords.map((c) => c.y2)),
    page: coords[0].page,
  }
}

function mergeHorizontalCoords(coords: Coords[]): Coords[] {
  if (!coords || coords.length === 0) return []
  if (coords.length === 1) return coords

  const heights = coords.map((c) => c.y2 - c.y1)
  const avgHeight = heights.reduce((a, b) => a + b, 0) / heights.length
  const yTolerance = avgHeight * 0.5

  const sorted = [...coords].sort((a, b) => {
    const yDiff = a.y1 - b.y1
    if (Math.abs(yDiff) < yTolerance) {
      return a.x1 - b.x1
    }
    return yDiff
  })

  const merged: Coords[] = []
  let currentLine: Coords[] = [sorted[0]]

  for (let i = 1; i < sorted.length; i++) {
    const curr = sorted[i]
    const lastInLine = currentLine[currentLine.length - 1]

    const yOverlap = !(curr.y1 > lastInLine.y2 || curr.y2 < lastInLine.y1)
    const ySimilar = Math.abs(curr.y1 - lastInLine.y1) < yTolerance

    if (yOverlap || ySimilar) {
      currentLine.push(curr)
    } else {
      merged.push(mergeCoordsGroup(currentLine))
      currentLine = [curr]
    }
  }
  merged.push(mergeCoordsGroup(currentLine))

  return merged
}

export function groupCoordsIntoBlocks(coords: Coords[]): Coords[][] {
  if (!coords || coords.length === 0) return []

  const mergedLines = mergeHorizontalCoords(coords)

  if (mergedLines.length === 1) return [[mergedLines[0]]]

  const sorted = [...mergedLines].sort((a, b) => a.y1 - b.y1)

  const heights = sorted.map((c) => c.y2 - c.y1)
  const avgHeight = heights.reduce((a, b) => a + b, 0) / heights.length
  const gapThreshold = avgHeight * 1.5

  const blocks: Coords[][] = []
  let currentBlock: Coords[] = [sorted[0]]

  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1]
    const curr = sorted[i]
    const gap = curr.y1 - prev.y2

    if (gap > gapThreshold) {
      blocks.push(currentBlock)
      currentBlock = [curr]
    } else {
      currentBlock.push(curr)
    }
  }
  blocks.push(currentBlock)

  return blocks
}

export function generateBlockPath(
  coords: Pick<Coords, 'x1' | 'y1' | 'x2' | 'y2'>[],
  baseWidth: number,
  baseHeight: number,
  cornerRadius: number,
  padding: number
): string {
  if (!coords || coords.length === 0) return ''

  const toPercent = (val: number, base: number) => (val / base) * 100

  const sorted = [...coords]
    .sort((a, b) => a.y1 - b.y1)
    .map((coord) => ({
      x1: coord.x1 - padding,
      y1: coord.y1 - padding,
      x2: coord.x2 + padding,
      y2: coord.y2 + padding,
    }))

  const points: Point[] = []

  for (let i = 0; i < sorted.length; i++) {
    const coord = sorted[i]
    const left = toPercent(coord.x1, baseWidth)
    const top = toPercent(coord.y1, baseHeight)
    const bottom = toPercent(coord.y2, baseHeight)

    if (i === 0) {
      points.push({ x: left, y: top })
    } else {
      const prevCoord = sorted[i - 1]
      const prevLeft = toPercent(prevCoord.x1, baseWidth)
      const prevBottom = toPercent(prevCoord.y2, baseHeight)

      if (Math.abs(left - prevLeft) > 0.1) {
        const midY = (prevBottom + top) / 2
        points.push({ x: prevLeft, y: midY })
        points.push({ x: left, y: midY })
      }
      points.push({ x: left, y: top })
    }
    points.push({ x: left, y: bottom })
  }

  for (let i = sorted.length - 1; i >= 0; i--) {
    const coord = sorted[i]
    const right = toPercent(coord.x2, baseWidth)
    const top = toPercent(coord.y1, baseHeight)
    const bottom = toPercent(coord.y2, baseHeight)

    if (i === sorted.length - 1) {
      points.push({ x: right, y: bottom })
    } else {
      const nextCoord = sorted[i + 1]
      const nextRight = toPercent(nextCoord.x2, baseWidth)
      const nextTop = toPercent(nextCoord.y1, baseHeight)

      if (Math.abs(right - nextRight) > 0.1) {
        points.push({ x: nextRight, y: (bottom + nextTop) / 2 })
        points.push({ x: right, y: (bottom + nextTop) / 2 })
      }
      points.push({ x: right, y: bottom })
    }
    points.push({ x: right, y: top })
  }

  return buildRoundedPolygonPath(points, cornerRadius)
}

function buildRoundedPolygonPath(points: Point[], radius: number): string {
  if (!points || points.length < 3) return ''

  const n = points.length
  const pathParts: string[] = []

  for (let i = 0; i < n; i++) {
    const prev = points[(i - 1 + n) % n]
    const curr = points[i]
    const next = points[(i + 1) % n]

    const v1 = { x: curr.x - prev.x, y: curr.y - prev.y }
    const v2 = { x: next.x - curr.x, y: next.y - curr.y }

    const len1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y)
    const len2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y)

    if (len1 === 0 || len2 === 0) continue

    const maxR = Math.min(len1 / 2, len2 / 2, radius)

    const p1 = {
      x: curr.x - (v1.x / len1) * maxR,
      y: curr.y - (v1.y / len1) * maxR,
    }
    const p2 = {
      x: curr.x + (v2.x / len2) * maxR,
      y: curr.y + (v2.y / len2) * maxR,
    }

    if (i === 0) {
      pathParts.push(`M ${p1.x.toFixed(3)} ${p1.y.toFixed(3)}`)
    } else {
      pathParts.push(`L ${p1.x.toFixed(3)} ${p1.y.toFixed(3)}`)
    }

    pathParts.push(
      `Q ${curr.x.toFixed(3)} ${curr.y.toFixed(3)} ${p2.x.toFixed(3)} ${p2.y.toFixed(3)}`
    )
  }

  pathParts.push('Z')
  return pathParts.join(' ')
}
